import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiX, FiCheck, FiPlus, FiTrash2, FiZap, FiShield } = FiIcons;

const AssessmentEditor = ({ onClose, onSuccess, initialData }) => {
  const isEditing = !!initialData;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialData || {
    title: '',
    slug: '',
    category: 'strategy',
    complexity: 'intermediate',
    description: '',
    full_description: '',
    duration: '30 minutes',
    questions: 20,
    price_ai: 49,
    price_expert: 199,
    is_active: true,
    benefits: [''],
    sample_questions: ['']
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const dataToSave = {
      ...formData,
      benefits: formData.benefits.filter(b => b.trim() !== ''),
      sample_questions: formData.sample_questions.filter(q => q.trim() !== '')
    };

    let error;
    if (isEditing) {
      ({ error } = await supabase
        .from('assessments_1773012000000')
        .update(dataToSave)
        .eq('id', initialData.id));
    } else {
      ({ error } = await supabase
        .from('assessments_1773012000000')
        .insert([dataToSave]));
    }

    if (!error) {
      onSuccess();
    } else {
      alert(error.message);
      setLoading(false);
    }
  };

  const handleArrayChange = (field, index, value) => {
    const newArr = [...formData[field]];
    newArr[index] = value;
    setFormData({ ...formData, [field]: newArr });
  };

  const addArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayItem = (field, index) => {
    const newArr = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArr });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h2 className="text-2xl font-black text-gray-900 font-ubuntu">{isEditing ? 'Edit Assessment' : 'New Assessment'}</h2>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">Catalog Item Configuration</p>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-gray-200 rounded-full text-gray-400 transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 md:p-12 space-y-10">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest">Basic Information</h3>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Assessment Title</label>
                <input 
                  required
                  type="text" 
                  className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-bold"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value, slug: e.target.value.toLowerCase().replace(/ /g, '-') })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">URL Slug</label>
                <input 
                  required
                  type="text" 
                  className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-mono text-xs"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Category</label>
                  <select 
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="strategy">Strategy</option>
                    <option value="operations">Operations</option>
                    <option value="marketing">Marketing</option>
                    <option value="finance">Finance</option>
                    <option value="sales">Sales</option>
                    <option value="hr">Human Resources</option>
                    <option value="technology">Technology</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Complexity</label>
                  <select 
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold"
                    value={formData.complexity}
                    onChange={(e) => setFormData({ ...formData, complexity: e.target.value })}
                  >
                    <option value="basic">Basic</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-xs font-black text-purple-600 uppercase tracking-widest">Pricing & Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">AI Price ($)</label>
                  <div className="relative">
                    <SafeIcon icon={FiZap} className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-400" />
                    <input 
                      required
                      type="number" 
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-bold"
                      value={formData.price_ai}
                      onChange={(e) => setFormData({ ...formData, price_ai: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Expert Price ($)</label>
                  <div className="relative">
                    <SafeIcon icon={FiShield} className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400" />
                    <input 
                      required
                      type="number" 
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-bold"
                      value={formData.price_expert}
                      onChange={(e) => setFormData({ ...formData, price_expert: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Duration</label>
                  <input 
                    type="text" 
                    placeholder="e.g. 20-30 mins"
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Questions</label>
                  <input 
                    type="number" 
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold"
                    value={formData.questions}
                    onChange={(e) => setFormData({ ...formData, questions: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <input 
                  type="checkbox" 
                  id="is_active" 
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <label htmlFor="is_active" className="text-sm font-bold text-gray-700">Display in public catalog</label>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Marketing Content</h3>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Short Description</label>
              <textarea 
                required
                className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm h-20 resize-none"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Full Sales Description</label>
              <textarea 
                className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm h-32 resize-none"
                value={formData.full_description}
                onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Key Benefits</label>
                <button type="button" onClick={() => addArrayItem('benefits')} className="text-blue-600 hover:text-blue-700 text-xs font-bold flex items-center gap-1">
                  <SafeIcon icon={FiPlus} /> Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.benefits.map((benefit, i) => (
                  <div key={i} className="flex gap-2">
                    <input 
                      type="text" 
                      className="flex-1 px-4 py-2 bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      value={benefit}
                      onChange={(e) => handleArrayChange('benefits', i, e.target.value)}
                    />
                    <button type="button" onClick={() => removeArrayItem('benefits', i)} className="p-2 text-gray-300 hover:text-red-500 transition-colors">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sample Questions</label>
                <button type="button" onClick={() => addArrayItem('sample_questions')} className="text-blue-600 hover:text-blue-700 text-xs font-bold flex items-center gap-1">
                  <SafeIcon icon={FiPlus} /> Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.sample_questions.map((question, i) => (
                  <div key={i} className="flex gap-2">
                    <input 
                      type="text" 
                      className="flex-1 px-4 py-2 bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      value={question}
                      onChange={(e) => handleArrayChange('sample_questions', i, e.target.value)}
                    />
                    <button type="button" onClick={() => removeArrayItem('sample_questions', i)} className="p-2 text-gray-300 hover:text-red-500 transition-colors">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </form>

        <div className="p-8 bg-gray-50 border-t border-gray-100 flex justify-end gap-4">
          <button 
            type="button"
            onClick={onClose}
            className="px-8 py-3 bg-white border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-100 transition-all"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={loading}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? 'Saving...' : (
              <><SafeIcon icon={FiCheck} /> {isEditing ? 'Update Catalog Item' : 'Publish to Catalog'}</>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AssessmentEditor;